import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Button,
  LinearProgress,
  Alert,
  IconButton,
  Avatar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import {
  Refresh,
  Pending,
  CheckCircle,
  Schedule,
  Star
} from '@mui/icons-material';
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Brush
} from 'recharts';
import { ResponsivePie } from '@nivo/pie';
import { reportsAPI } from '../services/api';
import AIDashboard from '../components/AI/AIDashboard';
import EnhancedMetricCard from '../components/Dashboard/EnhancedMetricCard';
import {
  DashboardMetrics,
  ConversationTrendData,
  ChannelDistribution,
  AgentPerformanceMetric,
  CSATOverview
} from '../types/dashboard';

const DashboardPage: React.FC = () => {
  const navigate = useNavigate();

  // State management
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());

  // Data state
  const [metrics, setMetrics] = useState<DashboardMetrics | null>(null);
  const [conversationTrends, setConversationTrends] = useState<ConversationTrendData[]>([]);
  const [channelDistribution, setChannelDistribution] = useState<ChannelDistribution[]>([]);
  const [topAgents, setTopAgents] = useState<AgentPerformanceMetric[]>([]);
  const [csatOverview, setCSATOverview] = useState<CSATOverview | null>(null);

  // Date ranges - Backend expects YYYY-MM-DD format only
  const getLast7Days = () => ({
    start_date: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    end_date: new Date().toISOString().split('T')[0]
  });

  const getLast30Days = () => ({
    start_date: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    end_date: new Date().toISOString().split('T')[0]
  });

  const getLast90Days = () => ({
    start_date: new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    end_date: new Date().toISOString().split('T')[0]
  });

  /**
   * Fetch all dashboard data
   */
  const fetchDashboardData = async () => {
    setLoading(true);
    setError(null);

    try {
      // Parallel API calls for better performance
      const [
        metricsData,
        trendsData,
        channelsData,
        agentsData,
        csatData
      ] = await Promise.all([
        reportsAPI.getDashboardMetrics(),
        reportsAPI.getConversationTrends(getLast30Days()),
        reportsAPI.getChannelDistribution(getLast30Days()),
        reportsAPI.getTopAgents(getLast30Days(), 5),
        reportsAPI.getCSATOverview(getLast30Days())
      ]);

      setMetrics(metricsData);
      // Sort conversation trends by date to ensure proper x-axis ordering
      const sortedTrends = Array.isArray(trendsData)
        ? [...trendsData].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
        : [];
      setConversationTrends(sortedTrends);
      setChannelDistribution(channelsData);
      setTopAgents(agentsData);
      setCSATOverview(csatData);
      setLastUpdated(new Date());
    } catch (err: any) {
      console.error('Dashboard load error:', err);
      setError(err.message || 'Failed to load dashboard data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();

    // Auto-refresh every 60 seconds
    const interval = setInterval(() => {
      fetchDashboardData();
    }, 60000); // 60 seconds

    return () => clearInterval(interval);
  }, []);

  // Generate sparkline data from conversation trends
  const generateSparklineData = (dataKey: 'new_conversations' | 'resolved_conversations') => {
    return conversationTrends.map(item => ({
      value: item[dataKey] || 0
    }));
  };

  // Chart colors
  const COLORS = ['#1f93ff', '#4caf50', '#ff9800', '#f44336', '#9c27b0'];

  if (loading && !metrics) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <LinearProgress />
        <Typography align="center" sx={{ mt: 2 }}>
          Loading dashboard...
        </Typography>
      </Container>
    );
  }

  if (error && !metrics) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error" action={
          <Button color="inherit" size="small" onClick={fetchDashboardData}>
            Retry
          </Button>
        }>
          {error}
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" gutterBottom>
            Dashboard
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Last updated: {lastUpdated.toLocaleTimeString()}
          </Typography>
        </Box>
        <IconButton onClick={fetchDashboardData} disabled={loading}>
          <Refresh />
        </IconButton>
      </Box>

      {loading && <LinearProgress sx={{ mb: 2 }} />}

      <Grid container spacing={3}>
        {/* Enhanced Metric Cards with Sparklines */}
        <Grid item xs={12} sm={6} md={3}>
          <EnhancedMetricCard
            title="Open Conversations"
            value={metrics?.open_conversations || 0}
            color="#ff9800"
            icon={<Pending />}
            sparklineData={generateSparklineData('new_conversations')}
            trend={5.2}
            trendDirection="up"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <EnhancedMetricCard
            title="Pending"
            value={metrics?.pending_conversations || 0}
            color="#f44336"
            icon={<Schedule />}
            sparklineData={conversationTrends.map(item => ({ value: item.new_conversations || 0 }))}
            trend={-2.4}
            trendDirection="down"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <EnhancedMetricCard
            title="Unassigned"
            value={metrics?.unassigned_conversations || 0}
            color="#9c27b0"
            icon={<Pending />}
            sparklineData={conversationTrends.map(item => ({ value: Math.floor((item.new_conversations || 0) * 0.3) }))}
            trend={0.8}
            trendDirection="up"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <EnhancedMetricCard
            title="Resolution Rate"
            value={Math.round(metrics?.resolution_rate || 0)}
            color="#4caf50"
            icon={<CheckCircle />}
            suffix="%"
            sparklineData={generateSparklineData('resolved_conversations')}
            trend={3.1}
            trendDirection="up"
          />
        </Grid>

        {/* AI Dashboard */}
        <Grid item xs={12}>
          <AIDashboard />
        </Grid>

        {/* Conversation Trends - Enhanced Interactive AreaChart - Fixed: 2025-11-19 - Added empty state */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Conversation Trends (Last 7 Days)
            </Typography>
            {conversationTrends.length === 0 ? (
              <Box
                height={350}
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                sx={{ backgroundColor: '#f5f5f5', borderRadius: 2 }}
              >
                <Typography variant="body1" color="text.secondary" gutterBottom>
                  No conversation data available yet
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Data will appear once conversations are created
                </Typography>
              </Box>
            ) : (
              <ResponsiveContainer width="100%" height={350}>
                <AreaChart data={conversationTrends}>
                <defs>
                  <linearGradient id="colorNew" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#1f93ff" stopOpacity={0.8}/>
                    <stop offset="95%" stopColor="#1f93ff" stopOpacity={0.1}/>
                  </linearGradient>
                  <linearGradient id="colorResolved" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#4caf50" stopOpacity={0.8}/>
                    <stop offset="95%" stopColor="#4caf50" stopOpacity={0.1}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                <XAxis
                  dataKey="date"
                  stroke="#666"
                  style={{ fontSize: '12px' }}
                />
                <YAxis
                  stroke="#666"
                  style={{ fontSize: '12px' }}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#fff',
                    border: '1px solid #ccc',
                    borderRadius: '8px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                  }}
                  formatter={(value: number) => [value, '']}
                  labelStyle={{ fontWeight: 'bold', marginBottom: '5px' }}
                />
                <Legend
                  wrapperStyle={{ paddingTop: '10px' }}
                  iconType="circle"
                />
                <Area
                  type="monotone"
                  dataKey="new_conversations"
                  stroke="#1f93ff"
                  fillOpacity={1}
                  fill="url(#colorNew)"
                  name="New Conversations"
                  strokeWidth={3}
                  animationDuration={1000}
                />
                <Area
                  type="monotone"
                  dataKey="resolved_conversations"
                  stroke="#4caf50"
                  fillOpacity={1}
                  fill="url(#colorResolved)"
                  name="Resolved"
                  strokeWidth={3}
                  animationDuration={1000}
                />
                <Brush
                  dataKey="date"
                  height={30}
                  stroke="#1f93ff"
                  fill="#f5f5f5"
                />
              </AreaChart>
            </ResponsiveContainer>
            )}
          </Paper>
        </Grid>

        {/* Channel Distribution - Enhanced Interactive Pie Chart */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Channel Distribution
            </Typography>
            <Box height={350}>
              <ResponsivePie
                data={
                  Array.isArray(channelDistribution)
                    ? channelDistribution.map((item, index) => ({
                        id: item.channel_name,
                        label: item.channel_name,
                        value: item.total_conversations,
                        color: COLORS[index % COLORS.length]
                      }))
                    : []
                }
                margin={{ top: 40, right: 80, bottom: 80, left: 80 }}
                innerRadius={0.5}
                padAngle={0.7}
                cornerRadius={3}
                activeOuterRadiusOffset={8}
                borderWidth={1}
                borderColor={{
                  from: 'color',
                  modifiers: [['darker', 0.2]]
                }}
                arcLinkLabelsSkipAngle={10}
                arcLinkLabelsTextColor="#333333"
                arcLinkLabelsThickness={2}
                arcLinkLabelsColor={{ from: 'color' }}
                arcLabelsSkipAngle={10}
                arcLabelsTextColor={{
                  from: 'color',
                  modifiers: [['darker', 2]]
                }}
                colors={{ datum: 'data.color' }}
                enableArcLabels={true}
                enableArcLinkLabels={true}
                animate={true}
                motionConfig="gentle"
                transitionMode="innerRadius"
                tooltip={({ datum }) => (
                  <Box
                    sx={{
                      background: '#fff',
                      padding: '9px 12px',
                      border: '1px solid #ccc',
                      borderRadius: '4px',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                    }}
                  >
                    <Typography variant="body2" fontWeight="bold">
                      {datum.label}
                    </Typography>
                    <Typography variant="caption">
                      {datum.value} conversations ({((datum.value / channelDistribution.reduce((sum, item) => sum + item.total_conversations, 0)) * 100).toFixed(1)}%)
                    </Typography>
                  </Box>
                )}
              />
            </Box>
          </Paper>
        </Grid>

        {/* Top Performing Agents - Fixed: 2025-11-19 - Added empty state */}
        <Grid item xs={12} md={7}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Top Performing Agents
            </Typography>
            {topAgents.length === 0 ? (
              <Box
                height={250}
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                sx={{ backgroundColor: '#f5f5f5', borderRadius: 2 }}
              >
                <Typography variant="body1" color="text.secondary" gutterBottom>
                  No agent performance data available yet
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Data will appear once agents start handling conversations
                </Typography>
              </Box>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Agent</TableCell>
                      <TableCell align="right">Conversations</TableCell>
                      <TableCell align="right">Messages</TableCell>
                      <TableCell align="right">Resolutions</TableCell>
                      <TableCell align="right">CSAT</TableCell>
                      <TableCell align="right">Avg Response</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {topAgents.map((agent) => (
                    <TableRow key={agent.agent_id}>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Avatar src={agent.avatar_url} sx={{ width: 24, height: 24 }}>
                            {agent.agent_name.charAt(0)}
                          </Avatar>
                          {agent.agent_name}
                        </Box>
                      </TableCell>
                      <TableCell align="right">{agent.conversations_handled}</TableCell>
                      <TableCell align="right">{agent.messages_sent}</TableCell>
                      <TableCell align="right">{agent.resolutions}</TableCell>
                      <TableCell align="right">
                        <Box display="flex" alignItems="center" justifyContent="flex-end" gap={0.5}>
                          <Star sx={{ fontSize: 16, color: '#ffc107' }} />
                          {agent.avg_csat_score.toFixed(1)}
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        {agent.avg_response_time.toFixed(1)} min
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            )}
          </Paper>
        </Grid>

        {/* CSAT Overview */}
        <Grid item xs={12} md={5}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Customer Satisfaction
            </Typography>
            <Box textAlign="center" mb={3}>
              <Typography variant="h2" color="primary">
                {csatOverview?.average_rating.toFixed(1) || 'N/A'}
              </Typography>
              <Box display="flex" justifyContent="center" gap={0.5}>
                {[1, 2, 3, 4, 5].map((star) => (
                  <Star
                    key={star}
                    sx={{
                      color: star <= (csatOverview?.average_rating || 0) ? '#ffc107' : '#e0e0e0',
                      fontSize: 24
                    }}
                  />
                ))}
              </Box>
              <Typography variant="caption" color="text.secondary">
                Based on {csatOverview?.total_responses || 0} responses
              </Typography>
            </Box>
            {csatOverview && Array.isArray(csatOverview.rating_distribution) && csatOverview.rating_distribution.map((rating) => (
              <Box key={rating.rating} mb={1}>
                <Box display="flex" justifyContent="space-between" mb={0.5}>
                  <Typography variant="caption">
                    {rating.rating} Star{rating.rating !== 1 && 's'}
                  </Typography>
                  <Typography variant="caption">
                    {rating.count} ({rating.percentage.toFixed(1)}%)
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={rating.percentage}
                  sx={{ height: 8, borderRadius: 4 }}
                />
              </Box>
            ))}
          </Paper>
        </Grid>

        {/* Quick Actions - Fixed: 2025-11-19 - Use proper routing instead of href */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom sx={{ px: 1 }}>
              Quick Actions
            </Typography>
            <Box display="flex" gap={2} flexWrap="wrap">
              <Button
                variant="outlined"
                onClick={() => navigate('/dashboard/conversations')}
              >
                View All Conversations
              </Button>
              <Button
                variant="outlined"
                onClick={() => navigate('/dashboard/contacts')}
              >
                Manage Contacts
              </Button>
              <Button
                variant="outlined"
                onClick={() => navigate('/dashboard/reports')}
              >
                Create Report
              </Button>
              <Button
                variant="outlined"
                onClick={() => navigate('/dashboard/business-hours')}
              >
                Set Business Hours
              </Button>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default DashboardPage;
